/**
 * ADR-0234 — Sill agent credentials (Trust Engine rung S0).
 *
 * An AI company ("operator") enrols with Sill, proves control of its
 * domain, and registers Ed25519 public keys. Sill publishes the approved
 * keys in the Web Bot Auth directory format at
 *
 *   https://edge.sill.so/agents/<slug>/.well-known/http-message-signatures-directory
 *
 * and the operator's agents sign with
 *
 *   Signature-Agent: "https://edge.sill.so/agents/<slug>"
 *
 * This module holds the pure pieces shared by the origin (verification,
 * publishing, key intake) and the edge (recognising the header):
 *
 *   - the Sill-hosted Signature-Agent shape and its signer identity
 *     (`edge.sill.so/agents/<slug>` — never a bare host, so it can never
 *     collide with a signer host a bundled registry identity declares);
 *   - building the directory document (round-trips through
 *     `parseKeyDirectory` + `jwkThumbprint`);
 *   - strict public-key intake (JWK / raw base64(url) / hex / SPKI PEM),
 *     rejecting private-key material, non-canonical encodings and points
 *     outside the prime-order subgroup;
 *   - verifying a challenge-response request signature (RFC 9421 over
 *     `@method`, `@authority`, `@path`, `signature-agent`, with Sill's
 *     nonce in the signed `nonce` parameter). Nonce validity, single use
 *     and key status are the caller's job (origin), because they need
 *     state.
 *
 * Nothing here throws on bad input; every failure is a typed outcome.
 */
import { Point, verifyAsync } from '@noble/ed25519';
import { buildGeneralSignatureBase, decodeBase64, encodeBase64Url, jwkThumbprint, parseSignatureHeader, parseSignatureInput, } from './index.js';
/** The only host Sill serves operator directories from. */
export const SILL_AGENT_DIRECTORY_HOST = 'edge.sill.so';
/** Operator slug: 3–40 chars, lower-case letters, digits, inner hyphens. */
export const SILL_OPERATOR_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{1,38})[a-z0-9]$/;
export const SILL_DIRECTORY_SUFFIX = '/.well-known/http-message-signatures-directory';
/** Media type of a Web Bot Auth key directory. */
export const SILL_DIRECTORY_MEDIA_TYPE = 'application/http-message-signatures-directory+json';
/** Signer identity used everywhere a signer host is stored (`signed_agent`). */
export function sillSignerFor(slug) {
    return `${SILL_AGENT_DIRECTORY_HOST}/agents/${slug}`;
}
/** The value an agent puts (quoted) in `Signature-Agent`. */
export function sillSignatureAgentUrl(slug) {
    return `https://${SILL_AGENT_DIRECTORY_HOST}/agents/${slug}`;
}
export function sillDirectoryUrl(slug) {
    return `${sillSignatureAgentUrl(slug)}${SILL_DIRECTORY_SUFFIX}`;
}
/** Is a stored `signed_agent` value a Sill-hosted credential signer? Returns the slug or null. */
export function sillSlugFromSigner(signer) {
    if (typeof signer !== 'string')
        return null;
    const prefix = `${SILL_AGENT_DIRECTORY_HOST}/agents/`;
    const s = signer.trim().toLowerCase();
    if (!s.startsWith(prefix))
        return null;
    const slug = s.slice(prefix.length);
    return SILL_OPERATOR_SLUG_RE.test(slug) ? slug : null;
}
export function isSillCredentialSigner(signer) {
    return sillSlugFromSigner(signer) !== null;
}
/**
 * Parse a raw `Signature-Agent` header naming a Sill-hosted directory:
 * exactly `"https://edge.sill.so/agents/<slug>"` (sf-string, no query,
 * fragment, port, credentials or trailing path). Anything else → null.
 */
export function parseSillSignatureAgent(raw) {
    const m = /^\s*"([^"]{1,200})"\s*$/.exec(raw);
    if (m === null)
        return null;
    const prefix = `https://${SILL_AGENT_DIRECTORY_HOST}/agents/`;
    const v = m[1];
    if (!v.startsWith(prefix))
        return null;
    const slug = v.slice(prefix.length);
    if (!SILL_OPERATOR_SLUG_RE.test(slug))
        return null;
    return { slug, signer: sillSignerFor(slug) };
}
/** Build the published directory. `kid` is the RFC 7638 thumbprint (informational; verifiers match by thumbprint). */
export function buildSillKeyDirectory(keys) {
    const out = [];
    const seen = new Set();
    for (const k of keys) {
        const jwk = { kty: 'OKP', crv: 'Ed25519', x: k.x };
        const kid = jwkThumbprint(jwk);
        if (seen.has(kid))
            continue;
        seen.add(kid);
        out.push({ ...jwk, kid });
    }
    return { keys: out };
}
/** DER prefix of an Ed25519 SubjectPublicKeyInfo (RFC 8410): 30 2a 30 05 06 03 2b 65 70 03 21 00. */
const SPKI_PREFIX = [0x30, 0x2a, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x03, 0x21, 0x00];
function fromHex(s) {
    if (!/^[0-9a-fA-F]{64}$/.test(s))
        return null;
    const out = new Uint8Array(32);
    for (let i = 0; i < 32; i += 1)
        out[i] = parseInt(s.slice(i * 2, i * 2 + 2), 16);
    return out;
}
function validatePoint(bytes) {
    if (bytes.length !== 32)
        return { ok: false, reason: 'wrong_length' };
    let point;
    try {
        point = Point.fromBytes(bytes, false);
    }
    catch {
        return { ok: false, reason: 'off_curve_or_noncanonical' };
    }
    // Same two checks as @sill/mandate validateEd25519PublicKey: identity /
    // small-order points, then mixed-order points.
    if (point.isSmallOrder())
        return { ok: false, reason: 'small_order' };
    if (!point.isTorsionFree())
        return { ok: false, reason: 'not_prime_order' };
    const x = encodeBase64Url(bytes);
    return { ok: true, x, thumbprint: jwkThumbprint({ kty: 'OKP', crv: 'Ed25519', x }) };
}
function fromJwk(o) {
    if ('d' in o)
        return { ok: false, reason: 'private_key_supplied' };
    if (o['kty'] !== 'OKP' || o['crv'] !== 'Ed25519')
        return { ok: false, reason: 'wrong_key_type' };
    const x = o['x'];
    if (typeof x !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(x))
        return { ok: false, reason: 'malformed' };
    const bytes = decodeBase64(x);
    if (bytes === null)
        return { ok: false, reason: 'malformed' };
    return validatePoint(bytes);
}
/**
 * Accept an Ed25519 PUBLIC key as a JWK (object or JSON text), raw 32
 * bytes in base64 / base64url, 64 hex characters, or an SPKI PEM. Returns
 * the canonical base64url `x` and its thumbprint. Private keys (a JWK `d`,
 * a `PRIVATE KEY` PEM) are refused outright.
 */
export function parseEd25519PublicKeyInput(input) {
    if (typeof input === 'object' && input !== null && !Array.isArray(input)) {
        return fromJwk(input);
    }
    if (typeof input !== 'string')
        return { ok: false, reason: 'malformed' };
    const s = input.trim();
    if (s.length === 0 || s.length > 4096)
        return { ok: false, reason: 'malformed' };
    if (/PRIVATE KEY/.test(s))
        return { ok: false, reason: 'private_key_supplied' };
    if (s.startsWith('{')) {
        try {
            const j = JSON.parse(s);
            if (typeof j !== 'object' || j === null || Array.isArray(j))
                return { ok: false, reason: 'malformed' };
            return fromJwk(j);
        }
        catch {
            return { ok: false, reason: 'malformed' };
        }
    }
    const pem = /^-----BEGIN PUBLIC KEY-----\s*([A-Za-z0-9+/=\s]+?)\s*-----END PUBLIC KEY-----$/.exec(s);
    if (pem !== null) {
        const der = decodeBase64(pem[1]);
        if (der === null || der.length !== 44)
            return { ok: false, reason: 'wrong_key_type' };
        for (let i = 0; i < SPKI_PREFIX.length; i += 1)
            if (der[i] !== SPKI_PREFIX[i])
                return { ok: false, reason: 'wrong_key_type' };
        return validatePoint(der.slice(12));
    }
    const hex = fromHex(s);
    if (hex !== null)
        return validatePoint(hex);
    const b = decodeBase64(s);
    if (b === null)
        return { ok: false, reason: 'malformed' };
    return validatePoint(b);
}
// ---------------------------------------------------------------------------
// Challenge-response request verification
// ---------------------------------------------------------------------------
/** Components a challenge response must cover. */
export const SILL_CHALLENGE_COMPONENTS = ['@method', '@authority', '@path', 'signature-agent'];
/** Maximum `expires − created` for a challenge response, seconds. */
export const SILL_CHALLENGE_MAX_WINDOW_S = 300;
/**
 * Verify a request signed by a Sill-enrolled operator over the request
 * itself (method, authority, path, signature-agent). `msg.headers` must
 * carry `signature-agent`, `signature-input` and `signature` (lower-case
 * names). Only an ed25519 failure against an enrolled key is `invalid`.
 */
export async function verifySillSignedRequest(msg, deps) {
    const now = (deps.now ?? (() => Math.floor(Date.now() / 1000)))();
    const agent_raw = msg.headers['signature-agent'];
    const input_raw = msg.headers['signature-input'];
    const sig_raw = msg.headers['signature'];
    const agent = agent_raw === undefined ? null : parseSillSignatureAgent(agent_raw);
    const parsed = input_raw === undefined ? null : parseSignatureInput(input_raw);
    const covers_request = parsed !== null && SILL_CHALLENGE_COMPONENTS.every((c) => parsed.components.includes(c));
    const base_result = {
        slug: agent?.slug ?? null,
        keyid: parsed?.keyid ?? null,
        nonce: parsed?.nonce ?? null,
        created: parsed?.created ?? null,
        expires: parsed?.expires ?? null,
        covers_request,
    };
    const r = (outcome) => ({ outcome, ...base_result });
    if (agent === null || parsed === null || sig_raw === undefined)
        return r('malformed');
    if (parsed.alg !== 'ed25519' || parsed.tag !== 'web-bot-auth')
        return r('unsupported');
    if (parsed.keyid === null || parsed.created === null || parsed.expires === null)
        return r('malformed');
    if (!covers_request)
        return r('unsupported');
    if (parsed.created > now + (deps.max_created_skew_s ?? 60))
        return r('clock_skew');
    if (parsed.expires <= now)
        return r('expired');
    if (parsed.expires - parsed.created > (deps.max_window_s ?? SILL_CHALLENGE_MAX_WINDOW_S))
        return r('window_too_long');
    const base = buildGeneralSignatureBase(parsed, msg);
    if (base === null)
        return r('unsupported');
    const sig = parseSignatureHeader(sig_raw, parsed.label);
    if (sig === null || sig.length !== 64)
        return r('malformed');
    const keys = await deps.resolveKeys(agent.slug);
    if (keys === null)
        return r('not_enrolled');
    let pub = null;
    for (const k of keys) {
        if (jwkThumbprint({ kty: 'OKP', crv: 'Ed25519', x: k.x }) === parsed.keyid) {
            pub = decodeBase64(k.x);
            break;
        }
    }
    if (pub === null || pub.length !== 32)
        return r('unknown_key');
    let ok = false;
    try {
        ok = await verifyAsync(sig, new TextEncoder().encode(base), pub);
    }
    catch {
        ok = false;
    }
    return r(ok ? 'verified' : 'invalid');
}
/**
 * ADR-0234 review N4 — verify a detached Ed25519 signature over a UTF-8
 * message with a base64url public key `x`; the signature may be base64 or
 * base64url. Used for proof of possession at key intake. Never throws.
 */
export async function verifyEd25519Raw(x, message, signature) {
    const pub = decodeBase64(x);
    const sig = decodeBase64(signature.trim());
    if (pub === null || pub.length !== 32 || sig === null || sig.length !== 64)
        return false;
    try {
        return await verifyAsync(sig, new TextEncoder().encode(message), pub);
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=sill-credential.js.map